import {
  Box,
  Button,
  FormRow,
  FormSection,
  Loader,
  PositiveInteger,
  ReadOnlyValue,
  Space,
  unsafeLocalizedString,
  unsafePositiveInteger,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { boolean, either, option } from "fp-ts";
import { constNull, constVoid, flow, pipe } from "fp-ts/function";
import { Reader } from "fp-ts/Reader";
import { useState } from "react";
import {
  RuntimeLocaleKey,
  useFormatMessage,
  useFormatPercentageLike,
} from "../../intl";
import * as api from "../api";
import { ReadOnlyMoneyAmountField } from "../../Common/ReadOnlyMoneyAmountField/ReadOnlyMoneyAmountField";
import { InputSliderField } from "../../Common/InputSliderField/InputSliderField";

type Props = {
  currentLoanOffer: api.LoanOfferWithLimits & { isLoading: boolean };
  onChange: Reader<PositiveInteger, unknown>;
};

export function Tenor(props: Props) {
  const formatMessage = useFormatMessage();
  const formatPercentageLike = useFormatPercentageLike();
  const isMobileLayout = useIsMobileLayout();
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Box>
      <Box column grow>
        {pipe(
          isEditing,
          boolean.fold(constNull, () => (
            <>
              <span id={"minusAriaLabel"} className={"sr-only"}>
                {formatMessage("Slider.Minus.AriaLabel")}
              </span>
              <span id={"plusAriaLabel"} className={"sr-only"}>
                {formatMessage("Slider.Plus.AriaLabel")}
              </span>
              <InputSliderField
                value={props.currentLoanOffer.tenor}
                onChange={flow(
                  PositiveInteger.decode,
                  either.getOrElse(() => unsafePositiveInteger(1)),
                  props.onChange
                )}
                min={props.currentLoanOffer.minTenor}
                max={props.currentLoanOffer.maxTenor}
                step={unsafePositiveInteger(3)}
                syncInputWithSteps
                minLabelContent={() =>
                  formatMessage(
                    "PreApprovedLoanSaleConfigurator.minTenorLabel",
                    {
                      months: props.currentLoanOffer.minTenor,
                    }
                  )
                }
                minAriaLabel={() =>
                  formatMessage(
                    "PreApprovedLoanSaleConfigurator.minTenorAriaLabel",
                    {
                      months: props.currentLoanOffer.minTenor,
                    }
                  )
                }
                maxLabelContent={() =>
                  formatMessage(
                    "PreApprovedLoanSaleConfigurator.maxTenorLabel",
                    {
                      months: props.currentLoanOffer.maxTenor,
                    }
                  )
                }
                maxAriaLabel={() =>
                  formatMessage(
                    "PreApprovedLoanSaleConfigurator.maxTenorAriaLabel",
                    {
                      months: props.currentLoanOffer.maxTenor,
                    }
                  )
                }
                minusAriaLabelledBy={unsafeLocalizedString("minusAriaLabel")}
                plusAriaLabelledBy={unsafeLocalizedString("plusAriaLabel")}
                name="currentTenor"
                issues={option.none}
                label={formatMessage(
                  "PreApprovedLoanSaleConfigurator.tenorLabelTenor"
                )}
                onBlur={constVoid}
                rightContent={formatMessage(
                  "PreApprovedLoanSaleConfigurator.tenorSliderUnits"
                )}
                disabled={props.currentLoanOffer.isLoading}
              />
              <Space units={isMobileLayout ? 6 : 8} />
            </>
          ))
        )}
        <FormSection>
          {pipe(
            isEditing,
            boolean.fold(
              () => (
                <FormRow type="3-1">
                  <ReadOnlyValue
                    label={formatMessage(
                      "PreApprovedLoanSaleConfigurator.tenorLabelTenor"
                    )}
                    value={formatMessage(
                      "PreApprovedLoanSaleConfigurator.tenorDescriptionTenor",
                      {
                        months: props.currentLoanOffer.tenor,
                      }
                    )}
                  />
                  <Box style={{ justifyContent: "flex-end" }}>
                    <Button
                      variant="text"
                      size="default"
                      label={formatMessage(
                        "PreApprovedLoanSaleConfigurator.tenorEdit"
                      )}
                      action={() => setIsEditing(true)}
                    />
                  </Box>
                </FormRow>
              ),
              constNull
            )
          )}
          <FormRow type="full">
            {pipe(
              props.currentLoanOffer.isLoading,
              boolean.fold(
                () => (
                  <ReadOnlyMoneyAmountField
                    size="x-small"
                    label={formatMessage(
                      "PreApprovedLoanSaleConfigurator.tenorLabelInstalment"
                    )}
                    value={{
                      amount: props.currentLoanOffer.installment,
                      currency: props.currentLoanOffer.currency,
                    }}
                  />
                ),
                () => (
                  <Loader
                    ariaLabelMessage={formatMessage(
                      "generic.loading.alt" as RuntimeLocaleKey
                    )}
                  />
                )
              )
            )}
          </FormRow>
          <FormRow type="full">
            <ReadOnlyValue
              label={formatMessage(
                "PreApprovedLoanSaleConfigurator.tenorLabelInterestRate"
              )}
              value={formatMessage(
                "PreApprovedLoanSaleConfigurator.tenorDescriptionInterestRate",
                {
                  interestRate: formatPercentageLike(
                    props.currentLoanOffer.interestRate
                  ),
                }
              )}
            />
          </FormRow>
        </FormSection>
      </Box>
    </Box>
  );
}
